// framework
import { produce, immerable } from "immer";
// api
import * as Client from "../../../api/Client";
import { CallResultError } from "../../../api/callHelper";

export interface IReferenceDataViewModel {
    readonly honorifics: Client.GetCreatePublicFeedbackReferenceDataResponseHonorificDto[];
    readonly feedbackTypes: Client.GetCreatePublicFeedbackReferenceDataResponseFeedbackTypeDto[];
    readonly nopimsActivityTypes: Client.GetCreatePublicFeedbackReferenceDataResponseNopimsActivityTypeDto[];
    readonly activities: Client.GetCreatePublicFeedbackReferenceDataResponseActivityDto[];
    readonly dataMonitoringItemTypes: Client.GetCreatePublicFeedbackReferenceDataResponseDataMonitoringItemTypeDto[];
}

export interface IFeedbackViewModel {
    readonly honorific?: Client.GetCreatePublicFeedbackReferenceDataResponseHonorificDto | undefined;
    readonly customerFirstName?: string | undefined;
    readonly customerLastName?: string | undefined;
    readonly customerCompanyName?: string | undefined;
    readonly customerContactNumber?: string | undefined;
    readonly customerEmailAddress?: string | undefined;
    readonly feedbackType?: Client.GetCreatePublicFeedbackReferenceDataResponseFeedbackTypeDto | undefined;
    readonly subFeedbackType?: Client.GetCreatePublicFeedbackReferenceDataResponseSubFeedbackTypeDto | undefined;
    readonly nopimsActivityType?: Client.GetCreatePublicFeedbackReferenceDataResponseNopimsActivityTypeDto | undefined;
    readonly activity?: Client.GetCreatePublicFeedbackReferenceDataResponseActivityDto | undefined;
    readonly dataMonitoringItemType?: Client.GetCreatePublicFeedbackReferenceDataResponseDataMonitoringItemTypeDto | undefined;
    readonly customerComments?: string | undefined;
}

export interface IRootViewModel {
    readonly userToken: string;
    readonly statusMessages?: Client.StatusMessagesDto;
    readonly isSubmitted: boolean;
    readonly isDirty: boolean;
    readonly isBusy: boolean;
    readonly error: CallResultError | undefined;
    readonly feedback: IFeedbackViewModel;
    readonly referenceData: IReferenceDataViewModel;

    feedbackDetailsChanged(values: IFeedbackViewModel): IRootViewModel;
    feedbackDetailsReset(): IRootViewModel;
    referenceDataLoading(): IRootViewModel;
    referenceDataLoaded(response: Client.GetCreatePublicFeedbackReferenceDataResponseDto | undefined, error: CallResultError | undefined): IRootViewModel;
    feedbackRequestCreating(): IRootViewModel;
    feedbackRequestCreated(response: Client.CreatePublicFeedbackResponseDto | undefined, error: CallResultError | undefined): IRootViewModel;
}

export class RootViewModel implements IRootViewModel {
    [immerable] = true;

    constructor(userToken: string) {
        this.userToken = userToken;
        this.statusMessages = undefined;
        this.isSubmitted = false;
        this.isDirty = false;
        this.isBusy = false;
        this.error = undefined;
        this.feedback = {};
        this.referenceData = {
            honorifics: [],
            feedbackTypes: [],
            nopimsActivityTypes: [],
            activities: [],
            dataMonitoringItemTypes: [],
        };
    }

    public readonly userToken: string;
    public readonly statusMessages?: Client.StatusMessagesDto;
    public readonly isSubmitted: boolean;
    public readonly isDirty: boolean;
    public readonly isBusy: boolean;
    public readonly error: CallResultError | undefined;
    public readonly feedback: IFeedbackViewModel;
    public readonly referenceData: IReferenceDataViewModel;

    public feedbackDetailsChanged(values: IFeedbackViewModel): IRootViewModel {
        return produce(this, (draft) => {
            draft.feedback.honorific = values.honorific;
            draft.feedback.customerFirstName = values.customerFirstName;
            draft.feedback.customerLastName = values.customerLastName;
            draft.feedback.customerCompanyName = values.customerCompanyName;
            draft.feedback.customerContactNumber = values.customerContactNumber;
            draft.feedback.customerEmailAddress = values.customerEmailAddress;
            draft.feedback.feedbackType = values.feedbackType;
            draft.feedback.subFeedbackType = values.subFeedbackType;
            draft.feedback.nopimsActivityType = values.nopimsActivityType;
            draft.feedback.activity = values.activity;
            draft.feedback.dataMonitoringItemType = values.dataMonitoringItemType;
            draft.feedback.customerComments = values.customerComments;
            draft.isDirty = true;
        });
    }

    public feedbackDetailsReset(): IRootViewModel {
        return produce(this, (draft) => {
            draft.statusMessages = undefined;
            draft.isSubmitted = false;
            draft.isDirty = false;
            draft.error = undefined;
            draft.feedback = {};
        });
    }

    public referenceDataLoading(): IRootViewModel {
        return produce(this, (draft) => {
            draft.isBusy = true;
        });
    }

    public referenceDataLoaded(response: Client.GetCreatePublicFeedbackReferenceDataResponseDto | undefined, error: CallResultError | undefined): IRootViewModel {
        return produce(this, (draft) => {
            if (response) {
                draft.referenceData.honorifics = response.honorifics;
                draft.referenceData.feedbackTypes = response.feedbackTypes;
                draft.referenceData.nopimsActivityTypes = response.nopimsActivityTypes;
                draft.referenceData.activities = response.activities;
                draft.referenceData.dataMonitoringItemTypes = response.dataMonitoringItemTypes;
            }
            draft.isBusy = false;
            draft.error = error;
        });
    }

    public feedbackRequestCreating(): IRootViewModel {
        return produce(this, (draft) => {
            draft.isBusy = true;
        });
    }

    public feedbackRequestCreated(response: Client.CreatePublicFeedbackResponseDto | undefined, error: CallResultError | undefined) {
        return produce(this, (draft) => {
            if (response) {
                draft.statusMessages = response.statusMessages;
                if (response.statusMessages.isSuccess) {
                    draft.isSubmitted = true;
                    draft.isDirty = false;
                }
            }
            draft.isBusy = false;
            draft.error = error;
        });
    }
}
